/**
 * 日志工具 - 统一的日志管理
 * 
 * 功能特性：
 * - 开发环境输出到控制台
 * - 生产环境发送到监控服务
 * - 支持不同日志级别
 * - 自动收集上下文信息
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  timestamp: string;
  level: LogLevel;
  message: string;
  data?: unknown;
  url?: string;
  userAgent?: string;
}

/**
 * 发送日志到监控服务
 */
const sendToMonitoring = (context: LogContext): void => {
  // TODO: 集成监控服务 (Sentry, LogRocket, 自定义后端等)
  // 示例实现：
  // fetch('/api/logs', {
  //   method: 'POST',
  //   headers: { 'Content-Type': 'application/json' },
  //   body: JSON.stringify(context),
  // }).catch(() => {
  //   // 静默失败，避免日志记录本身导致错误
  // });
  
  // 开发环境也输出到控制台以便调试
  if (import.meta.env.DEV) {
    console.log('[Logger]', context);
  }
};

/**
 * 创建日志上下文
 */
const createContext = (level: LogLevel, message: string, data?: unknown): LogContext => {
  return {
    timestamp: new Date().toISOString(),
    level,
    message,
    data,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  };
};

/**
 * 日志工具类
 */
export const logger = {
  /**
   * 调试日志 - 仅开发环境
   */
  debug: (message: string, data?: unknown): void => {
    if (import.meta.env.DEV) {
      console.debug(`[DEBUG] ${message}`, data);
    }
  },

  /**
   * 信息日志
   */
  info: (message: string, data?: unknown): void => {
    const context = createContext('info', message, data);
    
    if (import.meta.env.DEV) {
      console.info(`[INFO] ${message}`, data);
    } else {
      sendToMonitoring(context);
    }
  },

  /**
   * 警告日志
   */
  warn: (message: string, data?: unknown): void => {
    const context = createContext('warn', message, data);
    
    if (import.meta.env.DEV) {
      console.warn(`[WARN] ${message}`, data);
    } else {
      sendToMonitoring(context);
    }
  },

  /**
   * 错误日志
   */
  error: (message: string, error?: Error | unknown): void => {
    const context = createContext('error', message, {
      error: error instanceof Error ? {
        message: error.message,
        stack: error.stack,
        name: error.name,
      } : error,
    });
    
    if (import.meta.env.DEV) {
      console.error(`[ERROR] ${message}`, error);
    }
    
    // 生产环境总是发送错误日志
    if (import.meta.env.PROD) {
      sendToMonitoring(context);
    }
  },

  /**
   * 性能日志
   */
  performance: (metric: string, value: number, unit = 'ms'): void => {
    const message = `Performance: ${metric} = ${value}${unit}`;
    const context = createContext('info', message, { metric, value, unit });
    
    if (import.meta.env.DEV) {
      console.log(`[PERF] ${message}`);
    } else {
      sendToMonitoring(context);
    }
  },
};

export default logger;
